import { FormEvent, ReactNode, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Trash2 } from "lucide-react";
import { getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  GiornoSenzaRitiro,
  TrasportatoreRitiri,
  creaGiornoSenzaRitiro,
  eliminaGiornoSenzaRitiro,
  giorniSenzaRitiro,
  msAdessoItalia,
  msDataAznext,
  trasportatoriRitiri,
} from "../../api/ritiri";
import { CardRitiri, PulsanteRitiri } from "../../components/ritiri/TestataRitiri";
import { BadgeEsito, giornoBreve, numero } from "./presentazione";
import { CalendarioEventi } from "./CalendarioEventi";
import {
  CALCOLO,
  CALENDARIO,
  DATE,
  ESITI,
  INTRO_ESITI,
  INTRO_GIORNI_SENZA_RITIRO,
  INTRO_STAGIONALITA,
  NOTA_STAGIONALITA_EVENTI,
  NOTA_STORICO,
  SCENARI,
  VoceTesto,
} from "./testiLegenda";

/**
 * Scheda "Regole" (§ 7 punto 4): esiti, calcolo e calendario spiegati in
 * chiaro, tabella della stagionalità, calendario eventi (§ 4 bis) e giorni
 * senza ritiro (aggiunta ed eliminazione con `ritiri:update`, v1.14).
 * I testi vengono da `testiLegenda.ts`, gli stessi della legenda e degli aiuti
 * in pagina. Sono spiegazioni: le regole vere stanno solo nel motore del
 * backend, e la stagionalità è quella che lui ha calcolato.
 */

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function Paragrafi({ children }: { children: ReactNode }) {
  return <div className="space-y-2.5 text-sm leading-relaxed text-ink">{children}</div>;
}

function Voce({ v }: { v: VoceTesto }) {
  return (
    <p>
      <strong>{v.titolo}</strong> {v.testo}
    </p>
  );
}

export function RegoleRitiri({
  stagionalita,
  eventiAttivi,
  onEventiCambiati,
}: {
  stagionalita: Record<string, (number | null)[]>;
  eventiAttivi: number;
  /** Dopo una modifica del calendario eventi: la pagina ricarica la situazione. */
  onEventiCambiati: () => void;
}) {
  return (
    <div className="space-y-6">
      <CardRitiri titolo="Esiti">
        <Paragrafi>
          <p>{INTRO_ESITI}</p>
        </Paragrafi>
        <ul className="mt-4 divide-y divide-ritiri-bordo rounded-xl border border-ritiri-bordo" data-regole="esiti">
          {ESITI.map((e) => (
            <li key={e.esito} className="grid gap-2 p-3 sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-start">
              <span>
                <BadgeEsito esito={e.esito} testo={e.nome} />
              </span>
              <span className="space-y-1 text-sm text-ink">
                <span className="block">{e.quando}</span>
                <span className="block">
                  <strong>Cosa fare:</strong> {e.cosaFare}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-ritiri-th-testo">{NOTA_STORICO}</p>
      </CardRitiri>

      <CardRitiri titolo="Calcolo">
        <Paragrafi>
          {CALCOLO.map((v) => (
            <Voce key={v.titolo} v={v} />
          ))}
          <ul className="list-disc space-y-1 pl-5">
            {SCENARI.map((v) => (
              <li key={v.titolo}>
                <strong>{v.titolo}:</strong> {v.testo}
              </li>
            ))}
          </ul>
        </Paragrafi>
      </CardRitiri>

      <CardRitiri titolo="Calendario">
        <Paragrafi>
          <p>{CALENDARIO.festivi}</p>
          <p>I calendari sono due:</p>
          <ul className="list-disc space-y-1.5 pl-5">
            {CALENDARIO.dueCalendari.map((v) => (
              <li key={v.titolo}>
                <strong>{v.titolo}</strong>: {v.testo}
              </li>
            ))}
          </ul>
          <ul className="space-y-1.5" data-regole="date">
            {(["D", "P1", "P2", "E"] as const).map((sigla) => (
              <li key={sigla}>
                <strong>{DATE[sigla].titolo}</strong>: {DATE[sigla].testo}
              </li>
            ))}
          </ul>
          <p>{CALENDARIO.scadenzaD}</p>
          <p>{CALENDARIO.base}</p>
        </Paragrafi>
      </CardRitiri>

      <CardRitiri titolo="Stagionalità per categoria">
        <TabellaStagionalita stagionalita={stagionalita} eventiAttivi={eventiAttivi} />
      </CardRitiri>

      <CardRitiri titolo="Calendario eventi">
        <CalendarioEventi onCambiato={onEventiCambiati} />
      </CardRitiri>

      <CardRitiri titolo="Giorni senza ritiro">
        <GiorniSenzaRitiro />
      </CardRitiri>
    </div>
  );
}

function TabellaStagionalita({ stagionalita, eventiAttivi }: { stagionalita: Record<string, (number | null)[]>; eventiAttivi: number }) {
  const categorie = Object.entries(stagionalita);
  return (
    <div>
      <p className="mb-3 text-sm text-ink">{INTRO_STAGIONALITA}</p>
      {eventiAttivi > 0 && <p className="mb-3 text-sm text-ink">{NOTA_STAGIONALITA_EVENTI}</p>}
      <div className="overflow-x-auto rounded-xl border border-ritiri-bordo">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr>
              <th scope="col" className="bg-ritiri-th-fondo px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">
                Categoria
              </th>
              {MESI.map((m) => (
                <th key={m} scope="col" className="bg-ritiri-th-fondo px-2 py-2.5 text-right text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categorie.map(([categoria, indici]) => {
              const neutra = indici.every((v) => v === null);
              return (
                <tr key={categoria} className="ritiri-riga border-t border-ritiri-bordo hover:bg-ritiri-hover">
                  <th scope="row" className="px-3 py-2 text-left font-semibold text-ink">
                    {categoria}
                    {neutra && <span className="ml-1.5 text-xs font-normal text-ritiri-th-testo">(neutra)</span>}
                  </th>
                  {indici.map((v, i) => (
                    <td
                      key={i}
                      className={clsx(
                        "px-2 py-2 text-right font-plex",
                        v === null ? "text-ritiri-th-testo" : v >= 1.15 ? "font-semibold text-ritiri-caldo" : v <= 0.85 ? "text-primary-light" : "text-ink"
                      )}
                    >
                      {v === null ? "—" : numero(v, 2)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const campo = "mt-1 h-10 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink";

function GiorniSenzaRitiro() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const puoModificare = hasPermission("ritiri:update");
  const [giorni, setGiorni] = useState<GiornoSenzaRitiro[] | null>(null);
  const [trasportatori, setTrasportatori] = useState<TrasportatoreRitiri[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [nuovo, setNuovo] = useState({ data: "", trasporto_valori_id: "", descrizione: "" });
  const [erroreNuovo, setErroreNuovo] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState<string | null>(null);

  const carica = useCallback(() => {
    let valida = true;
    Promise.all([giorniSenzaRitiro(), trasportatoriRitiri()])
      .then(([g, t]) => {
        if (!valida) return;
        setGiorni(g);
        setTrasportatori(t);
      })
      .catch((err) => valida && setErrore(getErrorMessage(err, "Impossibile caricare i giorni senza ritiro.")));
    return () => {
      valida = false;
    };
  }, []);
  useEffect(carica, [carica]);

  async function aggiungi(e: FormEvent) {
    e.preventDefault();
    setInCorso("nuovo");
    setErroreNuovo(null);
    try {
      await creaGiornoSenzaRitiro({
        data: nuovo.data,
        trasporto_valori_id: nuovo.trasporto_valori_id || undefined,
        descrizione: nuovo.descrizione.trim() || undefined,
      });
      setNuovo({ data: "", trasporto_valori_id: "", descrizione: "" });
      showToast("Giorno aggiunto.", "success");
      carica();
    } catch (err) {
      setErroreNuovo(getErrorMessage(err, "Impossibile aggiungere il giorno."));
    } finally {
      setInCorso(null);
    }
  }

  async function elimina(g: GiornoSenzaRitiro) {
    setInCorso(g.id);
    try {
      await eliminaGiornoSenzaRitiro(g.id);
      showToast("Giorno eliminato.", "success");
      carica();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il giorno."), "error");
    } finally {
      setInCorso(null);
    }
  }

  if (errore) return <p className="text-sm text-ink">{errore}</p>;
  if (!giorni) return <p className="text-sm text-ritiri-th-testo">Caricamento…</p>;

  const oggi = msAdessoItalia() - (msAdessoItalia() % 86_400_000);
  const serviti = new Map(trasportatori.map((t) => [t.id, t.casseforti_aznext]));
  const conCasseforti = (id: string) => {
    const n = serviti.get(id) ?? 0;
    return n === 1 ? "1 cassaforte AZNext" : `${n} casseforti AZNext`;
  };
  return (
    <div>
      <p className="mb-3 text-sm text-ink">{INTRO_GIORNI_SENZA_RITIRO}</p>
      {giorni.length === 0 ? (
        <p className="rounded-xl border border-ritiri-bordo px-3 py-6 text-center text-sm text-ritiri-th-testo">Nessun giorno senza ritiro inserito.</p>
      ) : (
        <ul className="divide-y divide-ritiri-bordo rounded-xl border border-ritiri-bordo" data-giorni>
          {giorni.map((g) => {
            const passato = (msDataAznext(`${g.data} 00:00`) ?? 0) < oggi;
            return (
              <li
                key={g.id}
                className={clsx("grid items-center gap-1 p-3 text-sm sm:grid-cols-[8rem_14rem_minmax(0,1fr)_auto]", passato && "text-ritiri-th-testo")}
              >
                <span className="font-plex font-semibold">
                  {giornoBreve(g.data)}/{g.data.slice(0, 4)}
                  {passato && <span className="ml-1 font-sans text-xs font-normal">(passato)</span>}
                </span>
                <span>
                  {g.trasporto_valori ? (
                    <>
                      {g.trasporto_valori.nome}
                      <span className="block text-xs text-ritiri-th-testo">{conCasseforti(g.trasporto_valori.id)}</span>
                    </>
                  ) : (
                    "Tutti i trasportatori"
                  )}
                </span>
                <span className="min-w-0 break-words">{g.descrizione ?? "—"}</span>
                {puoModificare && (
                  <button
                    type="button"
                    onClick={() => elimina(g)}
                    disabled={inCorso === g.id}
                    aria-label={`Elimina il giorno ${g.data}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-[11px] border border-ritiri-bordo-forte text-ink hover:bg-ritiri-hover disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {puoModificare && (
        <form onSubmit={aggiungi} className="mt-4 grid gap-3 rounded-xl border border-ritiri-bordo p-4 sm:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end" data-giorni="nuovo">
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Data
            <input type="date" required className={campo} value={nuovo.data} onChange={(e) => setNuovo((n) => ({ ...n, data: e.target.value }))} />
          </label>
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Trasportatore (facoltativo)
            <select className={campo} value={nuovo.trasporto_valori_id} onChange={(e) => setNuovo((n) => ({ ...n, trasporto_valori_id: e.target.value }))}>
              <option value="">Tutti i trasportatori</option>
              {trasportatori.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} ({conCasseforti(t.id)})
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Descrizione
            <input type="text" maxLength={255} className={campo} value={nuovo.descrizione} onChange={(e) => setNuovo((n) => ({ ...n, descrizione: e.target.value }))} />
          </label>
          <PulsanteRitiri type="submit" className="h-10" inCorso={inCorso === "nuovo"} disabled={!nuovo.data}>
            Aggiungi
          </PulsanteRitiri>
          {erroreNuovo && (
            <p className="text-sm text-danger sm:col-span-4" role="alert">
              {erroreNuovo}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
